import React, { useState, useEffect, useMemo } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import { DropdownMenu, DropdownItem, DropdownSeparator } from '../../components/ui/DropdownMenu';
import ShippingSetupWizard from '../../components/shipping/ShippingSetupWizard';
import { apiFetch } from '../../utils/api';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import {
  Truck, Plus, MoreVertical, Search, Edit2, Power, Trash2, Info,
} from 'lucide-react';
import './ShippingZones.css';

const ShippingZones = () => {
  const { isAuthenticated, loading: authLoading } = useSecureAuth();
  const { confirm } = useConfirm();

  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSetupWizardOpen, setIsSetupWizardOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]);
  const [stateSearch, setStateSearch] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [locationRates, setLocationRates] = useState([]);
  const [zoneForm, setZoneForm] = useState({
    name: '',
    shippingCost: '',
    freeShippingThreshold: '',
    estimatedDeliveryDays: '3',
    description: '',
  });

  const parseList = (value) => String(value || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  const normalizeLocationRates = (zone) => {
    const baseShipping = Number(zone?.shippingCost || 0);
    const raw = Array.isArray(zone?.locationRates) ? zone.locationRates : [];
    if (raw.length > 0) {
      return raw
        .map((item) => ({
          name: String(item?.name || item?.location || '').trim(),
          shippingCost: String(item?.shippingCost ?? baseShipping),
        }))
        .filter((item) => item.name);
    }

    return parseList(zone?.locations).map((name) => ({
      name,
      shippingCost: String(baseShipping),
    }));
  };

  const syncLocationRatesFromInput = () => {
    const names = parseList(locationInput);
    if (names.length === 0) return;

    setLocationRates((prev) => {
      const existing = new Set(prev.map((item) => item.name.toLowerCase()));
      const fallbackCost = zoneForm.shippingCost || '0';
      const additions = names
        .filter((name) => !existing.has(name.toLowerCase()))
        .map((name) => ({ name, shippingCost: String(fallbackCost) }));
      return [...prev, ...additions];
    });
    setLocationInput('');
  };

  const updateLocationRate = (index, field, value) => {
    setLocationRates((prev) => prev.map((item, idx) => (
      idx === index ? { ...item, [field]: value } : item
    )));
  };

  const removeLocationRate = (index) => {
    setLocationRates((prev) => prev.filter((_, idx) => idx !== index));
  };

  const filteredStates = useMemo(() => {
    const q = stateSearch.trim().toLowerCase();
    if (!q) return states;
    return states.filter((s) => s.toLowerCase().includes(q));
  }, [states, stateSearch]);

  const fetchZones = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!isAuthenticated) throw new Error('Please log in to access shipping zones');
      const response = await apiFetch('/shipping-zones', { credentials: 'include' });
      if (!response.ok) {
        let msg = `Failed to fetch shipping zones: ${response.status}`;
        try { msg += ` - ${await response.text()}`; } catch (e) {}
        throw new Error(msg);
      }
      const data = await response.json();
      setZones(data.zones || []);
    } catch (err) {
      console.error('Error fetching zones:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await apiFetch('/shipping-zones/states');
      const data = await response.json();
      setStates(data.states || []);
    } catch (err) {
      console.error('Failed to fetch states:', err);
    }
  };

  const saveZone = async () => {
    try {
      setLoading(true);
      const url = editingZone ? `/shipping-zones/${editingZone.id}` : '/shipping-zones';
      const method = editingZone ? 'PUT' : 'POST';
      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...zoneForm,
          states: selectedStates,
          locations: locationRates.map((item) => item.name),
          locationRates: locationRates.map((item) => ({
            name: item.name,
            shippingCost: Number(item.shippingCost) || Number(zoneForm.shippingCost) || 0,
          })),
        }),
      });
      if (!response.ok) {
        let msg = 'Failed to save zone';
        try {
          const errorData = await response.json();
          msg = errorData.error || msg;
        } catch (e) {
          try { msg = (await response.text()) || msg; } catch (e2) {
            msg = `HTTP ${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(msg);
      }
      await fetchZones();
      closeModals();
    } catch (err) {
      console.error('Error saving zone:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteZone = async (zoneId) => {
    const confirmed = await confirm('Are you sure you want to delete this shipping zone?', {
      title: 'Delete Shipping Zone',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await apiFetch(`/shipping-zones/${zoneId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        let msg = 'Failed to delete zone';
        try {
          const errorData = await response.json();
          msg = errorData.error || msg;
        } catch (e) {
          try { msg = (await response.text()) || msg; } catch (e2) {
            msg = `HTTP ${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(msg);
      }
      await fetchZones();
    } catch (err) {
      console.error('Error deleting zone:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleZoneStatus = async (zoneId, isActive) => {
    try {
      setLoading(true);
      const response = await apiFetch(`/shipping-zones/${zoneId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !isActive }),
      });
      if (!response.ok) throw new Error('Failed to update zone status');
      await fetchZones();
    } catch (err) {
      console.error('Error toggling zone status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openSetupWizard = () => setIsSetupWizardOpen(true);

  const handleWizardComplete = () => {
    fetchZones();
    setIsSetupWizardOpen(false);
  };

  const openCreateModal = () => {
    setEditingZone(null);
    setZoneForm({
      name: '',
      shippingCost: '',
      freeShippingThreshold: '',
      estimatedDeliveryDays: '3',
      description: '',
    });
    setSelectedStates([]);
    setStateSearch('');
    setLocationInput('');
    setLocationRates([]);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      name: zone.name,
      shippingCost: zone.shippingCost.toString(),
      freeShippingThreshold: zone.freeShippingThreshold.toString(),
      estimatedDeliveryDays: zone.estimatedDeliveryDays.toString(),
      description: zone.description || '',
    });
    setSelectedStates([...zone.states]);
    setStateSearch('');
    setLocationInput('');
    setLocationRates(normalizeLocationRates(zone));
    setIsEditModalOpen(true);
  };

  const closeModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingZone(null);
    setZoneForm({
      name: '',
      shippingCost: '',
      freeShippingThreshold: '',
      estimatedDeliveryDays: '3',
      description: '',
    });
    setSelectedStates([]);
    setStateSearch('');
    setLocationInput('');
    setLocationRates([]);
  };

  const handleStateToggle = (state) => {
    setSelectedStates((prev) =>
      prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state]
    );
  };

  const selectAllVisible = () => {
    const visible = new Set(filteredStates);
    setSelectedStates((prev) => Array.from(new Set([...prev, ...visible])));
  };

  const clearSelection = () => setSelectedStates([]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchZones();
      fetchStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (authLoading) {
    return (
      <Shell>
        <div className="sz-loading">Loading…</div>
      </Shell>
    );
  }

  if (!isAuthenticated) {
    return (
      <Shell>
        <div className="sz-loading">Please log in to manage shipping zones.</div>
      </Shell>
    );
  }

  const formCanSubmit = !loading && !!zoneForm.name && !!zoneForm.shippingCost && selectedStates.length > 0;

  return (
    <Shell>
      <div className="sz-page">

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="sz-header">
          <div className="sz-header-text">
            <h1 className="sz-title">Shipping zones</h1>
            <p className="sz-subtitle">Set rates and delivery times by state, town, or street.</p>
          </div>
          {zones.length > 0 && (
            <div className="sz-header-actions">
              <button
                type="button"
                className="sz-btn sz-btn-secondary"
                onClick={openSetupWizard}
                disabled={loading}
              >
                Bulk setup
              </button>
              <button
                type="button"
                className="sz-btn sz-btn-primary"
                onClick={openCreateModal}
                disabled={loading}
              >
                <Plus size={16} /> Add zone
              </button>
            </div>
          )}
        </div>

        {/* ── Error banner ───────────────────────────────────── */}
        {error && <div className="sz-banner sz-banner-error">{error}</div>}

        {/* ── Empty state ────────────────────────────────────── */}
        {zones.length === 0 ? (
          <div className="sz-empty">
            <div className="sz-empty-icon"><Truck size={22} /></div>
            <h2 className="sz-empty-title">No shipping zones yet</h2>
            <p className="sz-empty-desc">
              Create zones to group states and define location-specific delivery rates and times.
            </p>
            <div className="sz-empty-actions">
              <button
                type="button"
                className="sz-btn sz-btn-primary"
                onClick={openSetupWizard}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Set up zones'}
              </button>
              <button
                type="button"
                className="sz-btn sz-btn-ghost"
                onClick={openCreateModal}
                disabled={loading}
              >
                Or create one manually
              </button>
            </div>
          </div>
        ) : (
          /* ── Zone list ────────────────────────────────────── */
          <div className="sz-list">
            {zones.map((zone) => (
              <article key={zone.id} className="sz-zone">
                <div className="sz-zone-body">
                  <div className="sz-zone-head">
                    <div className="sz-zone-head-text">
                      <h3 className="sz-zone-title">
                        {zone.name}
                        <span className={`sz-pill ${zone.isActive ? 'sz-pill-active' : 'sz-pill-inactive'}`}>
                          {zone.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </h3>
                      <p className="sz-zone-meta">
                        {zone.states.length} state{zone.states.length !== 1 ? 's' : ''}
                        {Array.isArray(zone.locationRates) && zone.locationRates.length > 0
                          ? ` · ${zone.locationRates.length} location${zone.locationRates.length !== 1 ? 's' : ''}`
                          : Array.isArray(zone.locations) && zone.locations.length > 0
                            ? ` · ${zone.locations.length} location${zone.locations.length !== 1 ? 's' : ''}`
                            : ''}
                        {' · '}
                        {zone.estimatedDeliveryDays} day{zone.estimatedDeliveryDays !== 1 ? 's' : ''} delivery
                      </p>
                    </div>
                    <DropdownMenu
                      trigger={
                        <button
                          type="button"
                          className="sz-zone-actions"
                          aria-label={`Actions for ${zone.name}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      <DropdownItem onClick={() => openEditModal(zone)}>
                        <Edit2 size={14} style={{ marginRight: 8 }} /> Edit zone
                      </DropdownItem>
                      <DropdownItem onClick={() => toggleZoneStatus(zone.id, zone.isActive)}>
                        <Power size={14} style={{ marginRight: 8 }} />
                        {zone.isActive ? 'Deactivate' : 'Activate'}
                      </DropdownItem>
                      <DropdownSeparator />
                      <DropdownItem variant="danger" onClick={() => deleteZone(zone.id)}>
                        <Trash2 size={14} style={{ marginRight: 8 }} /> Delete zone
                      </DropdownItem>
                    </DropdownMenu>
                  </div>

                  <div className="sz-zone-stats">
                    <div className="sz-zone-stat">
                      <span className="sz-zone-stat-label">Shipping cost</span>
                      <p className="sz-zone-stat-value">₦{Number(zone.shippingCost).toLocaleString()}</p>
                    </div>
                    <div className="sz-zone-stat">
                      <span className="sz-zone-stat-label">Free over</span>
                      <p className="sz-zone-stat-value">₦{Number(zone.freeShippingThreshold).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="sz-zone-states">
                    <span className="sz-zone-states-label">States</span>
                    <div className="sz-state-pills">
                      {zone.states.map((state) => (
                        <span key={state} className="sz-state-pill">{state}</span>
                      ))}
                    </div>
                  </div>

                  {(Array.isArray(zone.locationRates) && zone.locationRates.length > 0) || (Array.isArray(zone.locations) && zone.locations.length > 0) ? (
                    <div className="sz-zone-states">
                      <span className="sz-zone-states-label">Locations</span>
                      <div className="sz-state-pills">
                        {(Array.isArray(zone.locationRates) && zone.locationRates.length > 0
                          ? zone.locationRates
                          : zone.locations.map((location) => ({ name: location, shippingCost: zone.shippingCost }))
                        ).map((location) => (
                          <span key={location.name} className="sz-state-pill">
                            {location.name} · ₦{Number(location.shippingCost || zone.shippingCost).toLocaleString()}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* ── Create / Edit modal ────────────────────────────── */}
        <Modal
          isOpen={isCreateModalOpen || isEditModalOpen}
          onClose={closeModals}
          title={editingZone ? 'Edit shipping zone' : 'Create shipping zone'}
          padded={false}
          footer={
            <>
              <button
                type="button"
                className="sz-btn sz-btn-secondary"
                onClick={closeModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sz-btn sz-btn-primary"
                onClick={saveZone}
                disabled={!formCanSubmit}
              >
                {editingZone ? 'Update zone' : 'Create zone'}
              </button>
            </>
          }
        >
          <div className="sz-form">
            <div className="sz-field">
              <label className="sz-field-label">Zone name</label>
              <input
                type="text"
                className="sz-input"
                value={zoneForm.name}
                onChange={(e) => setZoneForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Lagos Mainland"
              />
            </div>

            <div className="sz-form-row">
              <div className="sz-field">
                <label className="sz-field-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Shipping cost (₦)
                  <span
                    title="Required. Used as the fallback shipping cost when the customer’s street, city, town, or postcode does not match a location override."
                    style={{ display: 'inline-flex', alignItems: 'center', color: '#6b7280', cursor: 'help' }}
                  >
                    <Info size={13} />
                  </span>
                </label>
                <input
                  type="number"
                  className="sz-input"
                  value={zoneForm.shippingCost}
                  onChange={(e) => setZoneForm((p) => ({ ...p, shippingCost: e.target.value }))}
                  placeholder="1000"
                />
              </div>
              <div className="sz-field">
                <label className="sz-field-label">Free shipping over (₦)</label>
                <input
                  type="number"
                  className="sz-input"
                  value={zoneForm.freeShippingThreshold}
                  onChange={(e) => setZoneForm((p) => ({ ...p, freeShippingThreshold: e.target.value }))}
                  placeholder="50000"
                />
              </div>
            </div>

            <div className="sz-field">
              <label className="sz-field-label">Estimated delivery (days)</label>
              <input
                type="number"
                className="sz-input"
                value={zoneForm.estimatedDeliveryDays}
                onChange={(e) => setZoneForm((p) => ({ ...p, estimatedDeliveryDays: e.target.value }))}
                placeholder="3"
              />
            </div>

            <div className="sz-field sz-states-panel">
              <label className="sz-field-label">States</label>
              <div className="sz-states-search">
                <Search size={14} className="sz-states-search-icon" />
                <input
                  type="text"
                  className="sz-input"
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  placeholder="Search states…"
                />
              </div>
              <div className="sz-states-grid">
                {filteredStates.length === 0 ? (
                  <div className="sz-states-empty">No states match “{stateSearch}”.</div>
                ) : (
                  filteredStates.map((state) => {
                    const checked = selectedStates.includes(state);
                    return (
                      <label
                        key={state}
                        className={`sz-state-check${checked ? ' is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleStateToggle(state)}
                        />
                        <span className="sz-state-check-text">{state}</span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="sz-states-meta">
                <span>{selectedStates.length} selected</span>
                <span>
                  <button type="button" onClick={selectAllVisible}>
                    Select{stateSearch ? ' visible' : ' all'}
                  </button>
                  {selectedStates.length > 0 && (
                    <>
                      {' · '}
                      <button type="button" onClick={clearSelection}>Clear</button>
                    </>
                  )}
                </span>
              </div>
            </div>

            <div className="sz-field">
              <label className="sz-field-label">Description (optional)</label>
              <textarea
                className="sz-input sz-textarea"
                value={zoneForm.description}
                onChange={(e) => setZoneForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of this zone."
              />
            </div>

            <div className="sz-field">
              <label className="sz-field-label">Location overrides (optional)</label>
              <div className="sz-location-builder">
                <div className="sz-location-builder-row">
                  <input
                    type="text"
                    className="sz-input"
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        syncLocationRatesFromInput();
                      }
                    }}
                    placeholder="Type towns, streets, or postcodes, then press Enter"
                  />
                  <button
                    type="button"
                    className="sz-btn sz-btn-secondary"
                    onClick={syncLocationRatesFromInput}
                  >
                    Add
                  </button>
                </div>
                <p className="sz-location-help">
                  Each location uses its own shipping cost. If no location matches, the state shipping cost is used.
                </p>
                {locationRates.length > 0 && (
                  <div className="sz-location-rate-list">
                    {locationRates.map((item, index) => (
                      <div key={`${item.name}-${index}`} className="sz-location-rate-row">
                        <input
                          type="text"
                          className="sz-input"
                          value={item.name}
                          onChange={(e) => updateLocationRate(index, 'name', e.target.value)}
                          placeholder="Location name"
                        />
                        <input
                          type="number"
                          className="sz-input"
                          value={item.shippingCost}
                          onChange={(e) => updateLocationRate(index, 'shippingCost', e.target.value)}
                          placeholder="₦ cost"
                        />
                        <button
                          type="button"
                          className="sz-btn sz-btn-ghost"
                          onClick={() => removeLocationRate(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>

        {/* ── Setup wizard ───────────────────────────────────── */}
        <ShippingSetupWizard
          isOpen={isSetupWizardOpen}
          onClose={() => setIsSetupWizardOpen(false)}
          onComplete={handleWizardComplete}
          isAuthenticated={isAuthenticated}
          existingZones={zones}
        />
      </div>
    </Shell>
  );
};

export default ShippingZones;
